import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { EmptyRow } from '@/features/EmptyRow';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { ConfirmPopup, type ConfirmPopupData } from '@/kit/ConfirmPopup';
import { DangerZone } from '@/kit/DangerZone';
import { openDialog } from '@/kit/dialog';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { InfoHint } from '@/kit/InfoHint';
import { PageHeader } from '@/kit/PageHeader';
import { Select } from '@/kit/Select';
import { NotificationTextMessage } from '@/core/enums/notification';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { managementReportingService } from '@/core/services/managementReporting';
import type { ExportFormatCode } from '@/core/models/practice.models';
import {
  managementMoney,
  managementPercent,
  type BudgetGridResponse,
  type BudgetRow,
  type DimensionRow,
  type ManagementPeriodRow,
} from '@/core/models/management-reporting.models';
import {
  dimensionValuesOf,
  ExportMenu,
  guardClient,
  MatButton,
  useLiveState,
  useOnInit,
  useSelectedClient,
} from './shared';
import './Budgets.scss';

/** A grid row while it is edited; the budget cell is a free-text input until it is saved. */
interface BudgetEditRow extends BudgetRow {
  budgetInput: string;
}

interface State {
  dataSource: BudgetEditRow[];
  periods: ManagementPeriodRow[];
  dimensions: DimensionRow[];
  dimensionValues: { code: string; name: string }[];
  selectedPeriodId: string | null;
  selectedDimensionCode: string | null;
  selectedDimensionValue: string | null;
}

/** Private to the budgets screen (there are other, different `parseAmount`s): NaN is possible and blocks the save. */
function parseAmount(value: string): number {
  const text = (value ?? '').toString().trim().replace(/,/g, '');
  return text.length === 0 ? 0 : Number(text);
}

const apply = (grid: BudgetGridResponse): BudgetEditRow[] =>
  grid.rows.map((row) => ({ ...row, budgetInput: row.budgetAmount ? String(row.budgetAmount) : '' }));

const HINT =
  "Budgets are entered here (or imported from the template: account code with the budget as a debit or credit). Actual amounts come from the management period's trial balance snapshot, taken on Management Periods; they fill in once the snapshot is taken.";

/** Budget entry for one management period (Angular TaxreturnBudgetsComponent). */
export function TaxreturnBudgets() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const client = useSelectedClient();
  const [s, set, live] = useLiveState<State>({
    dataSource: [],
    periods: [],
    dimensions: [],
    dimensionValues: [],
    selectedPeriodId: null,
    selectedDimensionCode: null,
    selectedDimensionValue: null,
  });
  const selectedFile = useRef<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  /** In-flight flags: Save and Import cannot be sent twice. */
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  useOnInit(() => {
    if (!guardClient(client, navigate)) {
      return;
    }

    managementReportingService.getDimensions(client.id).then(
      (rows) => set({ dimensions: rows }),
      (error) => notifyError(error),
    );

    const requested = params.get('id');
    managementReportingService.getManagementPeriods(client.id).then(
      (rows) => {
        setPeriodsLoaded(true);
        set({
          periods: rows,
          selectedPeriodId: requested && rows.some((r) => r.id === requested) ? requested : (rows[0]?.id ?? null),
        });
        if (live.current.selectedPeriodId) {
          load();
        }
      },
      (error) => notifyError(error),
    );
  });

  function onDimensionChange(): void {
    set({
      dimensionValues: dimensionValuesOf(live.current.dimensions, live.current.selectedDimensionCode),
      selectedDimensionValue: null,
    });
  }

  function load(): void {
    const st = live.current;
    if (!st.selectedPeriodId) {
      notifyError('Select a management period first.');
      return;
    }
    managementReportingService
      .getBudgets(st.selectedPeriodId, st.selectedDimensionCode, st.selectedDimensionValue)
      .then(
        (grid) => set({ dataSource: apply(grid) }),
        (error) => notifyError(error),
      );
  }

  function onSave(): void {
    const st = live.current;
    if (!st.selectedPeriodId) {
      notifyError('Select an accounting period first.');
      return;
    }

    const lines = st.dataSource.map((row) => ({
      accountCode: row.accountCode,
      budgetAmount: parseAmount(row.budgetInput),
      note: row.note,
    }));

    if (lines.some((line) => isNaN(line.budgetAmount))) {
      notifyError('Every budget amount must be a number.');
      return;
    }

    setSaving(true);
    managementReportingService
      .saveBudgets({
        managementPeriodId: st.selectedPeriodId,
        dimensionCode: st.selectedDimensionCode,
        dimensionValue: st.selectedDimensionValue,
        replaceAll: true,
        lines,
      })
      .then(
        (grid) => {
          setSaving(false);
          set({ dataSource: apply(grid) });
          notifySuccess(NotificationTextMessage.successMessage);
        },
        (error) => {
          setSaving(false);
          notifyError(error);
        },
      );
  }

  /** #186: actual amounts come from the period's snapshot, taken on Management Periods. */
  const openManagementPeriods = () => navigate('/home/taxreturns/reports-managementperiods');

  function onImport(): void {
    const st = live.current;
    if (!st.selectedPeriodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    if (!selectedFile.current) {
      notifyError(NotificationTextMessage.nofileError);
      return;
    }

    setImporting(true);
    managementReportingService
      .importBudgets(st.selectedPeriodId, selectedFile.current, st.selectedDimensionCode, st.selectedDimensionValue)
      .then(
        (grid) => {
          setImporting(false);
          set({ dataSource: apply(grid) });
          selectedFile.current = null;
          setFileName('');
          if (fileInput.current) fileInput.current.value = '';
          notifySuccess('Budget imported.');
        },
        (error) => {
          setImporting(false);
          notifyError(error);
        },
      );
  }

  function onTemplate(): void {
    managementReportingService.downloadBudgetTemplate().then(undefined, (error) => notifyError(error));
  }

  /** Asks first: clearing deletes every budget amount of the selected period and dimension. */
  function onClear(): void {
    if (!live.current.selectedPeriodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    void openDialog<ConfirmPopupData, boolean>(ConfirmPopup, {
      data: {
        title: 'Clear budget',
        message: 'Delete every budget amount of the selected management period and dimension? This cannot be undone.',
      },
    })
      .afterClosed()
      .then((confirmed) => {
        if (confirmed === true) clearBudget();
      });
  }

  function clearBudget(): void {
    const st = live.current;
    // The confirmation only opens once a period is selected; this keeps the type honest.
    if (!st.selectedPeriodId) return;
    managementReportingService
      .deleteBudgets({
        managementPeriodId: st.selectedPeriodId,
        dimensionCode: st.selectedDimensionCode,
        dimensionValue: st.selectedDimensionValue,
        accountCodes: [],
      })
      .then(
        () => {
          notifySuccess('Budget cleared.');
          load();
        },
        (error) => notifyError(error),
      );
  }

  function exportBudgets(format: ExportFormatCode): void {
    const st = live.current;
    if (!st.selectedPeriodId) {
      notifyError('Select a management period first.');
      return;
    }
    managementReportingService
      .exportBudgets(st.selectedPeriodId, {
        format,
        dimensionCode: st.selectedDimensionCode,
        dimensionValue: st.selectedDimensionValue,
      })
      .then(undefined, (error) => notifyError(error));
  }

  const setBudgetInput = (index: number, budgetInput: string) =>
    set((current) => ({
      dataSource: current.dataSource.map((row, i) => (i === index ? { ...row, budgetInput } : row)),
    }));

  const columns: DataTableColumn<BudgetEditRow>[] = [
    { id: 'code', header: 'Code', headerClassName: 'w-100', cell: (row) => row.accountCode },
    { id: 'name', header: 'Account', cell: (row) => row.accountName },
    { id: 'type', header: 'Type', headerClassName: 'w-200', cell: (row) => row.accountTypeName },
    {
      id: 'budget',
      header: 'Budget (£ - GBP)',
      headerClassName: 'w-150',
      cell: (row, index) => (
        <InputField
          fieldClassName="w-150"
          inputMode="decimal"
          charMode="number"
          extraChars=",.-"
          value={row.budgetInput}
          onChange={(event) => setBudgetInput(index, event.target.value)}
        />
      ),
    },
    {
      id: 'actual',
      header: 'Actual',
      headerClassName: 'w-150 text-align-center',
      cellClassName: 'text-align-center',
      cell: (row) => managementMoney(row.actualAmount),
    },
    {
      id: 'variance',
      header: 'Variance',
      headerClassName: 'w-150 text-align-center',
      cellClassName: 'text-align-center',
      cell: (row) => managementMoney(row.variance),
    },
    {
      id: 'percent',
      header: 'Variance %',
      headerClassName: 'w-150 text-align-center',
      cellClassName: 'text-align-center',
      cell: (row) => managementPercent(row.variancePercent),
    },
  ];

  return (
    <SideMenuPage menu="reports" contentClassName="tr-budgets">
      {/* Design review 2 Nov 2026: Save / Cancel Changes sit in the sticky page header, not a fixed footer. */}
      <PageHeader title="Budgets">
        {/* #186: where the Actual column comes from - it is not typed in here. */}
        <InfoHint text={HINT} />
        <Button className="action-button secondary-button" onClick={load}>
          Cancel Changes
        </Button>
        <Button className="action-button primary-button" busy={saving} onClick={onSave}>
          Save
        </Button>
      </PageHeader>
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Select
              className="w-270"
              items={s.periods}
              bindLabel="name"
              bindValue="id"
              placeholder="Select Management Period"
              value={s.selectedPeriodId}
              onChange={(value) => {
                set({ selectedPeriodId: value as string | null });
                load();
              }}
            />
            <Select
              className="w-180"
              items={s.dimensions}
              bindLabel="name"
              bindValue="code"
              placeholder="All dimensions"
              value={s.selectedDimensionCode}
              onChange={(value) => {
                set({ selectedDimensionCode: value as string | null });
                onDimensionChange();
                load();
              }}
            />
            <Select
              className="w-180"
              items={s.dimensionValues}
              bindLabel="name"
              bindValue="code"
              placeholder="All members"
              value={s.selectedDimensionValue}
              onChange={(value) => {
                set({ selectedDimensionValue: value as string | null });
                load();
              }}
            />
          </div>
          <div className="content-header-right mb-1">
            {/* #185: the backend's shared file reader only opens .csv/.xlsx (ClosedXML can't read the old
                binary .xls format); offering .xls here let a well-formed file always fail on upload. */}
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.xlsx"
              hidden
              onChange={(event) => {
                const files = event.target.files;
                selectedFile.current = files && files.length > 0 ? files[0] : null;
                setFileName(selectedFile.current?.name ?? '');
              }}
            />
            <Button className="action-button secondary-button" onClick={() => fileInput.current?.click()}>
              <Icon name="attach_file" />
              <span>Choose file</span>
            </Button>
            <span className="budget-file-name" title={fileName}>
              {fileName || 'No file chosen'}
            </span>
            <MatButton className="action-button secondary-button" icon="file_upload" busy={importing} onClick={onImport}>
              <span>Import</span>
            </MatButton>
            <a className="link text-underline-none d-flex align-items-center" onClick={openManagementPeriods}>
              <Icon name="event_note" className="font-size-18" />
              Management Periods
            </a>
            <a className="link text-underline-none d-flex align-items-center" onClick={onTemplate}>
              <Icon name="file_download" className="font-size-18" />
              Template
            </a>
            <ExportMenu onExport={exportBudgets} />
          </div>
        </div>

        <div className="sidenav-table sidenav-table-input wp-100">
          <div className="mat-table-wrapper contact-detail-table">
            <DataTable
              stickyHeader
              columns={columns}
              rows={s.dataSource}
              rowKey={(_, index) => index}
              noDataMessage={
                periodsLoaded ? (
                  s.selectedPeriodId ? (
                    <EmptyRow
                      icon="savings"
                      title="No budget lines for this period yet"
                      hint="Enter amounts, or import them from the template."
                    />
                  ) : (
                    <EmptyRow
                      icon="event_note"
                      title="No management period selected"
                      hint="Select a management period to see its budget."
                    />
                  )
                ) : undefined
              }
            />
          </div>
        </div>
        {/* Clear budget asks for confirmation before it deletes. */}
        <DangerZone
          items={[
            {
              id: 'clear-budget',
              title: 'Clear budget',
              description:
                'Deletes every budget amount of the selected management period and dimension. This cannot be undone.',
              actionLabel: 'Clear budget',
              onAction: onClear,
            },
          ]}
        />
      </div>
    </SideMenuPage>
  );
}
